import React, { FC, useEffect, useState } from "react";
import { Modal } from "@shared/components/Modal";
import { ButtonPrimary, ButtonSecondary, Icon } from "@shared/components";
import "./index.scss";
import { PayPerLeadCounty } from "@containers/PayPerLead/interface";
import { useDispatch, useSelector } from "react-redux";
import { actions as PayPerLeadActions, actions, selectors as PayPerLeadSelectors } from "@containers/PayPerLead/store";
import { actions as sharedActions } from "@shared/store";
import { MODAL_TYPES } from "@shared/constants";
import { ReactComponent as MinusIcon } from "@assets/files/icons/general/minus-grey.svg";
import { ModalSize } from "@shared/interfaces";
import { getLeadLimit } from "@containers/PayPerLead/containers/PayPerLeadCountiesContainer/consts";

import { EditCountyPriceElement } from "./components/EditCountyPriceElement";

interface ModalMoveUserToAnotherCompanyProps {
  onClose: () => void;
  data?: {
    ids: number[];
    fetchData: () => void;
  };
}

export enum EDIT_TYPES {
  VALUE = "VALUE",
  INCREASE = "INCREASE",
  DECREASE = "DECREASE",
}

export const ModalEditCountyPrice: FC<ModalMoveUserToAnotherCompanyProps> = ({ onClose, data }) => {
  const dispatch = useDispatch();

  const countiesItems = useSelector(PayPerLeadSelectors.getPayPerLeadCounties());

  const [isDirty, setIsDirty] = useState(false);
  const [leadPriceType, setLeadPriceType] = useState<EDIT_TYPES>(EDIT_TYPES.VALUE);
  const [leadLimitType, setLeadLimitType] = useState<EDIT_TYPES>(EDIT_TYPES.VALUE);
  const [leadPrice, setLeadPrice] = useState<number>();
  const [leadLimit, setLeadLimit] = useState<number>();
  const [items, setItems] = useState<PayPerLeadCounty[]>([]);
  const [hideItems, setHideItems] = useState<boolean>(false);

  useEffect(() => {
    const itemsToShow = countiesItems?.items?.filter((i) => data?.ids?.includes(i.id)) || [];

    setItems(itemsToShow);
    setHideItems(itemsToShow.length > 1);
  }, [data?.ids, countiesItems?.items]);

  const close = () => {
    onClose();
    dispatch(actions.unselectAllCountyLeads());
  };

  const removeItem = (id: number) => {
    setItems((curr) => {
      if (curr.length === 1) {
        close();
      }

      return curr.filter((c) => c.id !== id);
    });
  };

  const apply = () => {
    const newData = items.map((i) => ({
      countyId: i.id,
      price: getNewPrice(i),
      oldPrice: +(i.price?.price || 0),
      limit: getNewLimit(i),
      oldLimit: i.price?.limit || 0,
    }));

    close();

    const shouldShowConfirm = items.some((i) => i.price?.price !== getNewPrice(i));

    if (shouldShowConfirm) {
      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.APPLY_COUNTY_PRICE,
          size: ModalSize.RESIZABLE,
          props: {
            data: {
              items: newData,
              fetchData: data?.fetchData,
            },
          },
        }),
      );
    } else {
      dispatch(
        PayPerLeadActions.applyNewPrices.request({
          items: newData || [],
          immediately: true,
          callback: () => {
            data?.fetchData();
            onClose();
          },
        }),
      );
    }
  };

  const getNewPrice = (item: PayPerLeadCounty) => {
    if (!leadPrice) {
      return +(item.price?.price || 0);
    }

    if (leadPriceType === EDIT_TYPES.VALUE) {
      return +leadPrice * 100;
    }

    if (leadPriceType === EDIT_TYPES.INCREASE) {
      return +(item.price?.price || 0) + +leadPrice * 100;
    }

    return +(item.price?.price || 0) - +leadPrice * 100;
  };

  const getNewLimit = (item: PayPerLeadCounty) => {
    const currentLimit = item.price?.limit || getLeadLimit(item.price);

    if (!leadLimit) {
      return currentLimit;
    }

    if (leadLimitType === EDIT_TYPES.VALUE) {
      return leadLimit;
    }

    if (leadLimitType === EDIT_TYPES.INCREASE) {
      return currentLimit + +leadLimit;
    }

    return currentLimit - +leadLimit;
  };

  // do not show error on initial load, only after limit/price change
  if (!isDirty && (leadLimit || leadPrice)) {
    setIsDirty(true);
  }

  return (
    <div className="modal-edit-county-price">
      <Modal.Section>
        <Modal.Title>Edit County(ies)</Modal.Title>
        <Modal.Content>
          <EditCountyPriceElement
            itemType="price"
            type={leadPriceType}
            setType={setLeadPriceType}
            value={leadPrice}
            setValue={setLeadPrice}
          />
          <EditCountyPriceElement
            itemType="limit"
            type={leadLimitType}
            setType={setLeadLimitType}
            value={leadLimit}
            setValue={setLeadLimit}
          />

          <div className="divider"></div>

          {hideItems ? (
            <div className="view-all-items" onClick={() => setHideItems(false)}>
              View all {items.length} selected counties
            </div>
          ) : (
            <div className="county-table">
              <div className="county-table-row">
                <div className="county-table-county">County</div>
                <div className="county-table-value">Current Limit</div>
                <div className="county-table-value">Upcoming Limit</div>
                <div className="county-table-value">Current Price</div>
                <div className="county-table-value">Upcoming Price</div>
                <div className="county-table-actions"></div>
              </div>
              {items.map((i) => (
                <div key={i.id} className="county-table-row table">
                  <div className="county-table-county">
                    {i.name} ({i.state?.abbreviation})
                  </div>
                  <div className="county-table-value">{getLeadLimit(i.price)}</div>
                  <div className="county-table-value ">
                    {getNewLimit(i) === getLeadLimit(i.price) ? <MinusIcon /> : getNewLimit(i)}
                  </div>
                  <div className="county-table-value">
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format((i.price?.price || 0) / 100)}
                  </div>
                  <div className="county-table-value">
                    {getNewPrice(i) !== i.price?.price ? (
                      Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(getNewPrice(i) / 100)
                    ) : (
                      <MinusIcon />
                    )}
                  </div>
                  {items.length > 1 ? (
                    <div onClick={() => removeItem(i.id)} className="county-table-actions">
                      <Icon type="close" />
                    </div>
                  ) : (
                    <div className="county-table-actions" />
                  )}
                </div>
              ))}
            </div>
          )}

          {isDirty && items.some((i) => getNewPrice(i) <= 0) && (
            <div className="error">Upcoming price can&apos;t be 0 or less</div>
          )}
          {isDirty && items.some((i) => getNewLimit(i) < 0) && (
            <div className="error">Upcoming limit can&apos;t be less than 0</div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <ButtonSecondary onClick={close}>Cancel</ButtonSecondary>
          <ButtonPrimary
            type="submit"
            onClick={apply}
            disabled={items.some((i) => getNewPrice(i) <= 0 || getNewLimit(i) < 0 || (!leadPrice && !leadLimit))}
          >
            Apply
          </ButtonPrimary>
        </Modal.Actions>
      </Modal.Section>
    </div>
  );
};
